import React from "react";
import { Link } from "react-router-dom";
import BrandLayout from "@/components/brand/BrandLayout";
import {
  Rise,
  Eyebrow,
  SectionHead,
  CTA,
  Stat,
  FeatureCard,
  GlowFrame,
} from "@/components/brand/ui";
import ChooseNextStep from "@/components/brand/ChooseNextStep";
import RevenueLeakAnimation from "@/components/brand/RevenueLeakAnimation";
import { LAYERS, VALUE_CHAIN, DELIVERABLES, PROCESS, TOTAL_PARAMS } from "@/data/guide";

const LEAKS = [
  {
    n: "01",
    tone: "cyan",
    title: "The Clarity Leak",
    body: "Your headline sounds impressive but the visitor still can't tell what you do, who it's for, or why you.",
  },
  {
    n: "02",
    tone: "violet",
    title: "The Trust Leak",
    body: "They understand the offer but don't believe the company. No proof, no specifics, no authority. Uncertainty kills action.",
  },
  {
    n: "03",
    tone: "magenta",
    title: "The Conversion Leak",
    body: "“Learn More” → generic page → another page → an eleven-field form. Nothing is broken. The journey is leaking intent.",
  },
  {
    n: "04",
    tone: "cyan",
    title: "The Performance Leak",
    body: "Visually impressive, technically expensive, and still making people wait. Technology in the way of the objective.",
  },
  {
    n: "05",
    tone: "violet",
    title: "The Qualification Leak",
    body: "You generate leads — the wrong ones. Sales burns cycles on people who were never going to buy.",
  },
  {
    n: "06",
    tone: "magenta",
    title: "The Business Leak",
    body: "Sometimes the website isn't the problem. If it isn't the bottleneck, we'll tell you. That's part of the audit.",
  },
];

const OFFERS = [
  {
    step: "01",
    tone: "cyan",
    name: "Website Revenue Audit",
    forWhom: "Founders, CEOs & growth leaders",
    problem:
      "You can see the website underperforms — but not where the revenue is actually leaking.",
    deliverables: [
      "Full 144-parameter / 13-layer diagnostic",
      "Prioritized opportunity matrix",
      "Recommended fixes + implementation roadmap",
      "Personal findings walkthrough",
    ],
    timeline: "72 hours",
    price: "$750",
    priceNote: "Standard working price",
    scopeBased: false,
    hidePrice: true,
    crosssell: "Leads into the Website Revenue Fix Sprint.",
  },
  {
    step: "02",
    tone: "violet",
    name: "Website Revenue Fix Sprint",
    forWhom: "Teams ready to act on the findings",
    problem:
      "You have the diagnosis — now the highest-priority leaks need to be implemented, not just listed.",
    deliverables: [
      "Implements the highest-priority audit findings",
      "Across conversion, UX, messaging & trust",
      "Performance + measurement, in agreed scope",
    ],
    timeline: "30 days",
    price: "$1,500–$3,000",
    priceNote: "Depending on scope",
    scopeBased: false,
    hidePrice: true,
    crosssell: "Leads into a Growth / Revenue Engagement.",
  },
  {
    step: "03",
    tone: "magenta",
    name: "Growth / Revenue Engagement",
    forWhom: "Businesses compounding gains after the sprint",
    problem:
      "The fixes are live — now you want to keep implementing, experimenting, measuring and optimizing.",
    deliverables: [
      "Longer-term implementation & experimentation",
      "Ongoing measurement & optimization",
      "Scoped around what the audit and sprint surfaced",
    ],
    timeline: "3–6 months",
    price: "Scope-based",
    priceNote: "Typically $2,500+/mo · available after audit",
    scopeBased: true,
    crosssell: "Available after your audit & sprint, when it's the right next step.",
  },
];

const TONE_BAR = {
  cyan: "rgb(var(--brand-cyan))",
  violet: "rgb(var(--brand-violet))",
  magenta: "rgb(var(--brand-magenta))",
};

export default function Home() {
  return (
    <BrandLayout>
      {/* ============================================================ HERO */}
      <section
        className="relative overflow-hidden -mt-[var(--nav-h)] pt-[var(--nav-h)]"
        data-testid="home-hero"
      >
        <picture className="contents">
          {/* Keep this media selection in sync with the homepage preload in index.html.
              The mobile crop removes only pixels already outside the object-cover box. */}
          <source
            media="(max-width: 639px)"
            type="image/webp"
            srcSet="/images/hero-nexus-mobile.webp"
            width="480"
            height="768"
          />
          <source type="image/webp" srcSet="/images/hero-nexus.webp" />
          <img
            src="/images/hero-nexus.png"
            width="1376"
            height="768"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            data-testid="home-hero-image"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 z-0 h-full w-full object-cover opacity-[0.32]"
            style={{
              maskImage:
                "radial-gradient(120% 90% at 70% 0%, black 30%, transparent 78%)",
              WebkitMaskImage:
                "radial-gradient(120% 90% at 70% 0%, black 30%, transparent 78%)",
            }}
          />
        </picture>
        <div className="aurora" aria-hidden="true" />
        <div className="persp-grid" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1280px] px-5 sm:px-8 pt-10 pb-12 md:pt-24 md:pb-28">
          <div className="grid items-center gap-6 md:gap-12 lg:grid-cols-[1.05fr,0.95fr]">
            <div>
              <Rise>
                <Eyebrow>
                  13 Layers &middot; {TOTAL_PARAMS} Parameters &middot; 1 Objective
                </Eyebrow>
              </Rise>
              <Rise delay={90}>
                <h1 className="mt-4 font-display font-semibold text-[2.25rem] leading-[1.02] sm:mt-6 sm:text-[3.6rem] lg:text-[4.4rem] lg:leading-[0.98] text-white">
                  Your website is <span className="brand-text">leaking revenue</span>.
                  <br />
                  We find out where.
                </h1>
              </Rise>
              <Rise delay={170}>
                <p className="home-hero-copy mt-5 max-w-[52ch] text-base md:mt-7 md:text-lg leading-relaxed text-white/60">
                  A forensic diagnostic for founders and CEOs. We evaluate your website as a
                  commercial system &mdash; not a design &mdash; across 13 layers and{" "}
                  {TOTAL_PARAMS} parameters, then tell you the five things to fix first.
                </p>
              </Rise>
              <Rise delay={250}>
                <div className="mt-6 flex flex-wrap items-center gap-3.5 sm:mt-9">
                  <CTA to="/apply" testid="hero-apply-cta">
                    Claim a founding slot
                  </CTA>
                  <CTA to="/guide" testid="hero-guide-cta" variant="ghost">
                    Read the field guide
                  </CTA>
                </div>
              </Rise>
              <Rise delay={320}>
                <div className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-2 font-mono text-[9.5px] tracking-[0.16em] uppercase text-white/40 sm:mt-8">
                  <span className="inline-flex items-center gap-2">
                    <span aria-hidden="true" className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-400" />
                    <span><strong className="font-bold text-white">5</strong> founding cohort slots left</span>
                  </span>
                  <span className="hidden sm:inline">&middot;</span>
                  <span>Founding rate &middot; booking order</span>
                  <span className="hidden sm:inline">&middot;</span>
                  <span>Ecommerce &amp; DTC &middot; B2B &amp; SaaS &middot; Professional services</span>
                </div>
              </Rise>
            </div>

            <Rise delay={200} className="home-hero-art flex justify-center lg:justify-end">
              <RevenueLeakAnimation />
            </Rise>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 md:mt-16 md:grid-cols-4">
            <Stat value="13" label="Diagnostic layers" delay={0} />
            <Stat value={String(TOTAL_PARAMS)} label="Parameters checked" delay={70} />
            <Stat value="5" label="Priorities you act on" delay={140} />
            <Stat value="5" label="Founding cohort slots" delay={210} />
          </div>
        </div>
      </section>

      {/* ==================================================== VALUE CHAIN */}
      <section className="relative border-t border-white/[0.07] py-20 md:py-28">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
          <SectionHead
            eyebrow="The belief"
            title="Websites don't exist to look good. They exist to perform a function."
            sub="Your website sits inside a larger commercial chain. A failure anywhere in that chain creates a leak — and leaks compound quietly."
          />
          <div className="mt-12 overflow-hidden">
            <div className="marquee-track gap-3">
              {[...VALUE_CHAIN, ...VALUE_CHAIN].map((n, i) => (
                <div
                  key={`${n.label}-${i}`}
                  className="glass shrink-0 rounded-[8px] px-5 py-4 min-w-[210px]"
                >
                  <div className="font-mono text-[9px] tracking-[0.2em] text-white/35">
                    {String((i % VALUE_CHAIN.length) + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-1.5 font-display font-semibold text-lg text-white">
                    {n.label}
                  </div>
                  <div className="mt-1 text-[13px] leading-snug text-white/45">{n.leak}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================== LEAKS */}
      <section className="relative border-t border-white/[0.07] py-20 md:py-28">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[1.05fr,0.95fr] lg:items-center">
            <SectionHead
              eyebrow="What we find"
              title="Six leaks we see again and again."
              sub="None of them look broken. All of them cost money. Revenue enters the top of your funnel — and drips out through cracks nobody is watching."
              className="max-w-none"
            />
            <Rise delay={140}>
              <GlowFrame
                src="/images/infographic-funnel.png"
                alt="3D infographic of a five-tier glass conversion funnel leaking glowing revenue droplets through cracks at every stage"
                caption="Fig. 01 — How intent leaks out of a funnel"
                testid="home-funnel-visual"
              />
            </Rise>
          </div>
          <div className="mt-12 grid gap-3.5 md:grid-cols-2 lg:grid-cols-3">
            {LEAKS.map((l, i) => (
              <FeatureCard key={l.n} {...l} delay={i * 60} />
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================= LAYERS */}
      <section className="relative border-t border-white/[0.07] py-20 md:py-28">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.95fr,1.05fr] lg:items-center">
            <SectionHead
              eyebrow="The method"
              title="A 13-layer system, not an opinion."
              sub="Aesthetics are one parameter of 144. We examine business context, positioning, offer, messaging, UX, architecture, conversion, trust, content, SEO, performance, analytics and revenue."
              className="max-w-none"
            />
            <Rise delay={140}>
              <div className="glass rounded-[12px] p-5 sm:p-7">
                <div className="grid grid-cols-2 gap-x-6 gap-y-0 sm:grid-cols-2">
                  {LAYERS.map((l) => (
                    <div
                      key={l.n}
                      className="flex items-baseline gap-3 border-b border-white/[0.07] py-2.5"
                    >
                      <span className="font-mono text-[10px] tracking-[0.14em] text-[rgb(var(--brand-sky))]">
                        {l.n}
                      </span>
                      <span className="text-[14px] text-white/70">{l.name}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <CTA to="/method" testid="layers-method-cta" variant="ghost">
                    Explore the method
                  </CTA>
                </div>
              </div>
            </Rise>
          </div>
        </div>
      </section>

      {/* =================================================== DELIVERABLES */}
      <section className="relative border-t border-white/[0.07] py-20 md:py-28">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
          <SectionHead
            eyebrow="What you receive"
            title="Seven deliverables. Zero generic advice."
            center
          />
          <div className="mt-12 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            {DELIVERABLES.map((d, i) => (
              <FeatureCard
                key={d.n}
                n={d.n}
                title={d.n === "05" ? "Implementation Plan" : d.title}
                body={d.n === "05" ? "Prioritized steps to address the highest-impact findings." : d.body}
                delay={i * 55}
                tone={["cyan", "violet", "magenta"][i % 3]}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================== PROCESS */}
      <section className="relative border-t border-white/[0.07] py-20 md:py-28">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
          <SectionHead eyebrow="The process" title="From application to diagnosis in seven steps." />
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Rise key={p.n} delay={i * 55} className="h-full">
                <div className="glass tilt-card h-full rounded-[10px] p-5">
                  <div className="font-display font-semibold text-2xl brand-text">{p.n}</div>
                  <div className="mt-2.5 font-mono text-[10px] font-semibold tracking-[0.22em] uppercase text-white">
                    {p.title}
                  </div>
                  <p className="mt-2 text-[14px] leading-relaxed text-white/55">{p.body}</p>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================ OFFER ARCHITECTURE */}
      <section className="relative border-t border-white/[0.07] py-20 md:py-28" data-testid="offer-architecture">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
          <SectionHead
            eyebrow="The pathway"
            title="One audit. A clear path to growth."
            sub="Most engagements start with the audit, then move into a fix sprint, then — when it's the right next step — a longer growth engagement. You choose how far to go; each stage stands on its own."
          />

          <div className="mt-12 grid gap-3.5 lg:grid-cols-3" data-testid="offer-cards">
            {OFFERS.map((o, i) => (
              <Rise key={o.step} delay={i * 80} className="h-full">
                <div
                  className="glass tilt-card relative flex h-full flex-col overflow-hidden rounded-[12px] p-6 sm:p-7"
                  data-testid={`offer-card-${o.step}`}
                >
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 h-full w-[2px]"
                    style={{ background: TONE_BAR[o.tone] }}
                  />
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] font-semibold tracking-[0.22em] text-white/40">
                      STEP {o.step}
                    </span>
                    {o.scopeBased && (
                      <span className="rounded-[4px] border border-white/12 bg-white/[0.05] px-2.5 py-1 font-mono text-[8.5px] font-semibold tracking-[0.16em] uppercase text-white/60">
                        Scope-based
                      </span>
                    )}
                  </div>

                  <h3 className="mt-4 font-display font-semibold text-[1.35rem] leading-snug text-white">
                    {o.name}
                  </h3>

                  <div className="mt-4 font-mono text-[9px] font-semibold tracking-[0.2em] uppercase text-[rgb(var(--brand-sky))]">
                    Who it's for
                  </div>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-white/70">{o.forWhom}</p>

                  <div className="mt-4 font-mono text-[9px] font-semibold tracking-[0.2em] uppercase text-[rgb(var(--brand-sky))]">
                    The problem it solves
                  </div>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-white/55">{o.problem}</p>

                  <div className="mt-4 font-mono text-[9px] font-semibold tracking-[0.2em] uppercase text-[rgb(var(--brand-sky))]">
                    What you get
                  </div>
                  <ul className="mt-2 space-y-2">
                    {o.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-2.5">
                        <span
                          aria-hidden="true"
                          className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ background: TONE_BAR[o.tone] }}
                        />
                        <span className="text-[13.5px] leading-relaxed text-white/70">{d}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-5">
                    <div
                      className={`grid gap-px overflow-hidden rounded-[8px] border border-white/[0.08] bg-white/[0.06] ${
                        o.hidePrice ? "grid-cols-1" : "grid-cols-2"
                      }`}
                    >
                      <div className="bg-paper-sheet/60 p-3.5">
                        <div className="font-mono text-[8.5px] tracking-[0.14em] uppercase text-white/45">
                          Timeline
                        </div>
                        <div className="mt-1 font-medium text-[14px] text-white">{o.timeline}</div>
                      </div>
                      {!o.hidePrice && (
                        <div className="bg-paper-sheet/60 p-3.5">
                          <div className="font-mono text-[8.5px] tracking-[0.14em] uppercase text-white/45">
                            {o.scopeBased ? "Investment" : "Working price"}
                          </div>
                          <div
                            className="mt-1 font-display font-semibold text-[15px] brand-text"
                            data-testid={`offer-price-${o.step}`}
                          >
                            {o.price}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </Rise>
            ))}
          </div>

          <Rise delay={260}>
            <p className="mt-10 text-center font-mono text-[9.5px] tracking-[0.16em] uppercase text-white/35">
              Every pathway begins with the audit &middot; later stages are scoped to what it finds
            </p>
          </Rise>
        </div>
      </section>

      {/* ============================================================ NEXT STEP */}
      <ChooseNextStep
        eyebrow={<><strong className="font-bold text-white">5</strong> founding cohort slots left</>}
        eyebrowSignal
        intro="We’re running the TSM Nexus methodology through a founding cohort of five businesses."
      />
    </BrandLayout>
  );
}
